import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import { debounce } from 'lodash'
import PropTypes from 'prop-types'

import FeaturedTable from '../FeaturedTable/FeaturedTable'
import PaginationFooter from '../PaginationFooter/PaginationFooter'
import ConfirmPopup from '../Popups/ConfirmPopup'
import {
  selectInspectionSetups,
  fetchInspectionSetups,
  bulkDeleteInspectionSetups,
  updateInspectionSetup,
} from '../../reducers/inspectionsReducer'
import { selectActiveBuilding } from '../../reducers/buildingSelectorReducer'
import { useSnackbar } from '../../helpers/hooks/useSnackbar'

import {
  getInspectionFrequencyOptions,
  RECURRING_RECURRENCE_TYPES,
  recurringColumns,
} from './helpers'
import InternalInspectionsToolbar from './InternalInspectionsToolbar'

const InternalInspectionsRecurring = ({
  setups,
  building,
  fetchInspectionSetups = () => {},
  bulkDeleteInspectionSetups = () => {},
  updateInspectionSetup = () => {},
}) => {
  const { t } = useTranslation(['internal-inspections', 'common'])
  const [isLoading, setSiteLoading] = useState(false)
  const [currentFrequency, setCurrentFrequency] = useState(getInspectionFrequencyOptions(t)[0])
  const [showOnPage, setShowOnPage] = useState(25)
  const [pageNumber, setPageNumber] = useState(1)
  const [paginationItemsCount, setPaginationItemsCount] = useState(0)
  const [selectedRowsIds, setSelectedRowsIds] = useState([])
  const [clearSelectedRows, setClearSelectedRows] = useState(() => () => {})
  const [search, setSearch] = useState('')
  const [prevSearchValue, setPrevSearchValue] = useState('')
  const [isDeleteInspectionPopupOpen, setIsDeleteInspectionPopupOpen] = useState(false)
  const [isBulkDeleteLoading, setIsBulkDeleteLoading] = useState(false)
  const { displaySnackbar } = useSnackbar()
  const navigate = useNavigate()

  const handleSetPageChange = (pageNum) => {
    setPageNumber(pageNum)
  }

  const onDeleteInspection = async () => {
    setIsBulkDeleteLoading(true)

    try {
      await bulkDeleteInspectionSetups(selectedRowsIds)
      setIsDeleteInspectionPopupOpen(false)
      clearSelectedRows()
      fetchSetups()
      displaySnackbar(t('internal-inspections:recurring.deletedSuccessfully'))
    } catch {
      displaySnackbar(t('internal-inspections:recurring.errorDeleting'), true)
    }

    setIsBulkDeleteLoading(false)
  }

  const onSwitchStatus = async (rewardId, status) => {
    try {
      await updateInspectionSetup(rewardId, { enabled: status })
    } catch ({ message }) {
      displaySnackbar(message, true)
    }
  }

  const onRowSelect = useCallback((ids) => setSelectedRowsIds(ids), [])

  const fetchSetups = async (query) => {
    setSiteLoading(true)

    // Only reset to page 1 if we have an actual search query that's different from current search
    const isNewSearch = query !== undefined && query !== search

    const requestData = {
      buildingId: building.id,
      page: isNewSearch ? 1 : pageNumber,
      limit: showOnPage,
      search: query !== undefined ? query : search,
      recurrenceType: currentFrequency.value || RECURRING_RECURRENCE_TYPES,
    }

    await fetchInspectionSetups(requestData)
      .then((count) => setPaginationItemsCount(count))
      .catch(({ message }) => displaySnackbar(message, true))

    setSiteLoading(false)
  }

  const memoizedColumns = useMemo(
    () =>
      recurringColumns(
        { timezone: building ? building.timezone : '', onSwitch: onSwitchStatus },
        t,
      ),
    [building, t],
  )

  const debouncedFetch = useCallback(debounce(fetchSetups, 800), [showOnPage, currentFrequency])

  const openEdit = (id) => navigate(`/dashboard/building-dashboard/internal-inspections/edit/${id}`)

  const onRowClick = ({ original }) => {
    if (original.id) {
      openEdit(original.id)
    }
  }

  const onEdit = () => {
    if (selectedRowsIds.length === 1) {
      openEdit(selectedRowsIds[0])
    }
  }

  useEffect(() => {
    if (building) {
      fetchSetups()
    }
  }, [building, currentFrequency, pageNumber, showOnPage])

  useEffect(() => {
    if (prevSearchValue !== search) {
      window.addEventListener('keydown', debouncedFetch(search))
    }
    setPrevSearchValue(search)
    return () => {
      window.removeEventListener('keydown', debouncedFetch)
    }
  }, [search])

  return (
    <React.Fragment>
      <div className={`ctp-internal-inspections__container ${isLoading ? 'is-loading' : ''}`}>
        <InternalInspectionsToolbar
          onEdit={onEdit}
          onDelete={() => setIsDeleteInspectionPopupOpen(true)}
          search={search}
          onSearch={(e) => setSearch(e.target.value)}
          isLoading={isLoading}
          selectedRows={selectedRowsIds}
          componentOptions={{
            currentFrequency,
            onChangeFrequency: (e) => setCurrentFrequency(e.target.value),
          }}
        />
        <div className="internal-inspections-list-table">
          {setups.length ? (
            <FeaturedTable
              data={setups}
              columns={memoizedColumns}
              onChangeSelectedRows={onRowSelect}
              onRowClick={onRowClick}
              setClearSelectedRows={setClearSelectedRows}
            />
          ) : !isLoading ? (
            <div className="no-results">
              {t('internal-inspections:dashboard.noInspectionsFound')}
            </div>
          ) : null}
        </div>
        {setups.length ? (
          <PaginationFooter
            showOnPage={showOnPage}
            pageNumber={pageNumber}
            paginationItemsCount={paginationItemsCount}
            setSiteLoading={setSiteLoading}
            setShowOnPage={setShowOnPage}
            setPageNumber={handleSetPageChange}
          />
        ) : null}
      </div>
      <ConfirmPopup
        isOpen={isDeleteInspectionPopupOpen}
        isLoading={isBulkDeleteLoading}
        cancelCallback={() => setIsDeleteInspectionPopupOpen(false)}
        confirmCallback={onDeleteInspection}
        cancelText={t('common:actions.cancel')}
        confirmText={t('internal-inspections:dashboard.confirmDeleteInspections')}
        title={t('internal-inspections:dashboard.confirmationTitle')}
        body={t('internal-inspections:recurring.confirmDeleteBody')}
      />
    </React.Fragment>
  )
}

InternalInspectionsRecurring.propTypes = {
  setups: PropTypes.array,
  building: PropTypes.object,
  fetchInspectionSetups: PropTypes.func,
  bulkDeleteInspectionSetups: PropTypes.func,
  updateInspectionSetup: PropTypes.func,
}

export default connect(
  (state) => ({
    setups: selectInspectionSetups(state),
    building: selectActiveBuilding(state),
  }),
  { fetchInspectionSetups, bulkDeleteInspectionSetups, updateInspectionSetup },
)(InternalInspectionsRecurring)
